<script setup lang="ts">
import type { ITableHeader } from "@/components/ui/Table/table.interface"
import { computed } from "vue"
import { useListRsvpAttendees } from "@/api"
import { RsvpStatusPill } from "@/components"
import { Table } from "@/components/ui"
import { useFilters, usePagination, useTranslation } from "@/composables"
import { RsvpStatus } from "@/models"
import { useEventVaultStore } from "@/stores/eventVaultStore"

interface InvitationGuestListRow {
  id?: number
  displayName: string
  rsvpStatus?: RsvpStatus
}

const { n, t } = useTranslation()
const eventStore = useEventVaultStore()
const { filters } = useFilters({ page: 1, perPage: 10 })

const { data, isLoading, pageCount } = useListRsvpAttendees(eventStore.eventVaultId, filters)

const pagination = usePagination({ initialPerPage: filters.perPage, pageCount })

const guestListRows = computed<InvitationGuestListRow[]>(() => {
  return data.value?.rsvpGuestList?.map(guest => ({
    displayName: formatGuestListName(guest.name ?? "", guest.additionalGuestCount),
    rsvpStatus: RsvpStatus.ATTENDING,
  })) ?? []
})

const guestListHeaders = computed<ITableHeader<InvitationGuestListRow>[]>(() => [
  { label: t("invitation_page.guest_list_headers.guest"), value: "displayName" },
  { label: t("invitation_page.guest_list_headers.rsvp"), value: "rsvpStatus" },
])

function formatGuestListName(name: string, additionalGuests?: number) {
  if (!additionalGuests) {
    return name
  }

  return t("invitation_page.guest_list_name", {
    name,
    count: n(additionalGuests),
  }, additionalGuests)
}

function onPaginationNext() {
  pagination.next()
  filters.page = pagination.page.value
}

function onPaginationPrevious() {
  pagination.previous()
  filters.page = pagination.page.value
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-1 px-1">
      <h2 class="font-heading text-xl font-bold text-primary">
        {{ t("invitation_page.guest_list_title") }}
      </h2>
      <p class="font-body text-sm text-secondary-card-text">
        {{ t("invitation_page.guest_list_description") }}
      </p>
    </div>

    <Table
      :headers="guestListHeaders"
      :data="guestListRows"
      :is-loading="isLoading"
      :pagination="pagination"
      :no-data-headline="t('invitation_page.guest_list_empty_title')"
      :no-data-content="t('invitation_page.guest_list_empty_description')"
      dense
      show-pagination
      @pagination-next="onPaginationNext"
      @pagination-previous="onPaginationPrevious"
    >
      <template #data-rsvpStatus="{ data: row }">
        <RsvpStatusPill v-if="row.rsvpStatus" :status="row.rsvpStatus" />
      </template>
    </Table>
  </div>
</template>
